import { useDeepCompareMemo } from "use-deep-compare";
import { useCallback } from "react";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { SearchProductWithScore, SearchQuery } from "~/hooks/search/types";
import type { Table as TTable } from "~/shared/react-table/app-table";
import { BooksTable } from "~/components/books-table";

import {
  buildCatalogPayload,
  useCatalogAnalytics,
} from "~/pages/books/analytics";
import { TakeActionDropdown } from "./take-action-dropdown";
import { SearchToolbar } from "~/pages/books/components/search-toolbar";

export const BooksPageTable = ({
  table,
  isLoading,
  searchQuery,
  setSearchQuery,
  error,
  products,
  fetchAllResults,
  onBookClick,
  defaultPageSize,
  hasPagination,
  hasDetails,
  hasNoResultsBanner,
  ageRangeAvailableBounds,
}: {
  table: TTable<SearchProductWithScore>;
  isLoading: boolean;
  searchQuery: SearchQuery;
  setSearchQuery: (query: Partial<SearchQuery>) => void;
  error: Error | null | undefined;
  products: SearchProductWithScore[];
  fetchAllResults: () => Promise<SearchProductWithScore[]>;
  onBookClick?: (product: SearchProductWithScore, rowIndex: number) => void;
  defaultPageSize: number;
  hasPagination?: boolean;
  hasDetails?: boolean;
  hasNoResultsBanner?: boolean;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
}) => {
  const { emit: catalogEmit } = useCatalogAnalytics();
  const selectedTableIds = table.getSelectedIds();
  const selectedBooksCount = table.getSelectedCount();
  const isSelectedAll = table.getSelectedAll();
  const selectedIds = useDeepCompareMemo(
    () => selectedTableIds,
    [selectedTableIds],
  );
  const allBooksCount = table.getRowCount();
  const trimmedQuery = searchQuery.query?.trim() ?? "";
  const effectivePageSize = searchQuery.pageSize || defaultPageSize;

  const clearSelection = useCallback(
    () => table.toggleAllRowsSelected(false),
    [table],
  );

  const handleBookClick = useCallback(
    (product: SearchProductWithScore, rowIndex: number) => {
      const page = searchQuery.page ?? 1;
      catalogEmit.resultClicked(
        product.id,
        (page - 1) * effectivePageSize + rowIndex + 1,
        product.score ?? null,
        buildCatalogPayload(searchQuery),
      );
      if (onBookClick) {
        onBookClick(product, rowIndex);
      }
    },
    [onBookClick, catalogEmit, searchQuery, effectivePageSize],
  );

  return (
    <BooksTable
      table={table}
      isLoading={isLoading}
      hasPagination={hasPagination}
      onBookClick={handleBookClick}
      query={trimmedQuery}
      hasDetails={hasDetails}
      hasNoResultsBanner={hasNoResultsBanner}
      actions={
        <TakeActionDropdown
          disabled={selectedBooksCount === 0}
          isSelectedAll={isSelectedAll}
          selectedBooksCount={selectedBooksCount}
          selectedIds={selectedIds}
          allBooksCount={allBooksCount}
          products={products}
          searchQuery={searchQuery}
          fetchAllResults={fetchAllResults}
        />
      }
      header={
        <SearchToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          error={error}
          clearSelection={clearSelection}
          ageRangeAvailableBounds={ageRangeAvailableBounds}
        />
      }
    />
  );
};
